import React, { useMemo } from 'react'
import cls from 'classnames'
import { Link } from 'react-router-dom'
import { withTheme } from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'

import AccountSetup from 'containers/Navigation/AccountSetup'
import GroupSettingsMenu from 'containers/Navigation/GroupSettingsMenu'
import HelpMenu from 'containers/Navigation/HelpMenu'
import SystemNotifications from 'containers/Navigation/SystemNotifications'

import { MiniIcon } from 'components/lib/Badge'
import {
  Dmarc,
  Home,
  Incident,
  Mailbox,
  SimulationTraining,
  ThreatLog,
} from 'components/lib/DesignSystem/Icons'
import Tag, { TAGS_TYPES } from 'components/lib/DesignSystem/Tag'

import DropdownItem from './items/DropdownItem'
import IconItem from './items/IconItem'

require('components/styles/layouts/navigation.styl')

const isSelected = (pathname, url) =>
  Array.isArray(url) ? url.some((u) => pathname?.includes(u)) : pathname?.includes(url)

const getNavItemClass = (isSelected) => cls(isSelected ? 'selected typo-subtitle-2' : 'typo-body-1')

function NavigationLink({ id, pathname, children }) {
  return (
    <Link id={id} to={pathname} className="navigation-item-content">
      {children}
    </Link>
  )
}

const RegularNav = ({
  partner,
  companyName,
  userEmail,
  pathname,
  incidentsCount,
  userDropdownItems,
  hasLimitedPocView,

  incidentsVisibility,
  investigationVisibility,
  awarenessVisibility,
  mailboxesVisibility,
  groupSettingsVisibility,
  accountSetupVisibility,
  dmarcVisibility,
}) => {
  const homeSelected = isSelected(pathname, [URL_CONST.dashboard.home, URL_CONST.partners.self])
  const mailboxesSelected = isSelected(pathname, URL_CONST.mailboxes.self)
  const awarenessSelected =
    isSelected(pathname, URL_CONST.awarenessCenter.self) || isSelected(pathname, '/campaigns/')
  const rcSelected = isSelected(pathname, URL_CONST.incidents.self)
  const settingsSelected = isSelected(pathname, URL_CONST.settings.self)
  const groupSettingsSelected = isSelected(pathname, URL_CONST.groupSettings.self)
  const threatLogSelected = isSelected(pathname, URL_CONST.investigation.threatLog)
  const dmarcSelected = isSelected(pathname, URL_CONST.dmarc.self)

  const systemNotificationTitle = partner
    ? `System Notifications (${companyName})`
    : 'System Notifications'

  const MemoSystemNotifications = useMemo(
    () => <SystemNotifications title={systemNotificationTitle} />,
    [systemNotificationTitle]
  )

  const MemoHelpMenu = useMemo(() => <HelpMenu />, [])

  const MemoAccountSetup = useMemo(() => <AccountSetup />, [])

  const MemoGroupSettingsMenu = useMemo(() => <GroupSettingsMenu />, [])

  return (
    <>
      <div className="navigation-group center">
        <div
          className={cls('navigation-item', 'gt-p', getNavItemClass(homeSelected))}
          id="home_button"
        >
          <NavigationLink pathname={partner ? URL_CONST.partners.self : URL_CONST.dashboard.home}>
            <Home height="20px" width="20px" fill="white" />
            <span>Home</span>
          </NavigationLink>
        </div>

        {incidentsVisibility && (
          <div className={cls('navigation-item', getNavItemClass(rcSelected))}>
            <NavigationLink
              pathname={URL_CONST.incidents.unclassified}
              id="resolution_center_button"
            >
              <Incident height="20px" width="20px" fill="white" imageName="incident" />
              <span title="Incidents">Incidents</span>
              {!!incidentsCount && !hasLimitedPocView && (
                <MiniIcon top="0" left="auto" right="0">
                  {incidentsCount > 99 ? '99+' : incidentsCount}
                </MiniIcon>
              )}
            </NavigationLink>
          </div>
        )}

        {investigationVisibility && (
          <div className={cls('navigation-item', getNavItemClass(threatLogSelected), 'gt-t')}>
            <NavigationLink pathname={URL_CONST.investigation.threatLog} id="threat_log_button">
              <ThreatLog height="20px" width="20px" fill="white" imageName="threat-log" />
              <span>Investigation</span>
            </NavigationLink>
          </div>
        )}

        {awarenessVisibility && (
          <div
            className={cls('navigation-item', 'gt-p', getNavItemClass(awarenessSelected))}
            id="awareness_center_button"
          >
            <NavigationLink
              pathname={URL_CONST.awarenessCenter.campaigns}
              id="awareness_center_button"
            >
              <SimulationTraining
                height="20px"
                width="20px"
                fill="white"
                imageName="time_study_time_2"
              />
              <span title="Simulation & Training">Simulation & Training</span>
            </NavigationLink>
          </div>
        )}

        {mailboxesVisibility && (
          <div
            className={cls('navigation-item', 'gt-p', getNavItemClass(mailboxesSelected))}
            id="mailbox_management_button"
          >
            <NavigationLink
              pathname={URL_CONST.mailboxes.management}
              id="mailbox_management_button"
            >
              <Mailbox height="20px" width="20px" fill="white" imageName="mailbox" />
              <span title="Mailboxes">Mailboxes</span>
            </NavigationLink>
          </div>
        )}

        {dmarcVisibility && (
          <div
            className={cls('navigation-item', 'gt-p', getNavItemClass(dmarcSelected))}
            id="dmarc_navigation_button"
          >
            <NavigationLink pathname={URL_CONST.dmarc.self} id="dmarc_navigation_button">
              <Dmarc height="20px" width="20px" fill="white" imageName="dmarc" />
              <span title="DMARC">DMARC</span>
              <Tag type={TAGS_TYPES.NEW} style={{ marginLeft: '8px' }} />
            </NavigationLink>
          </div>
        )}
      </div>
      <div className="navigation-group right">
        <IconItem
          title={systemNotificationTitle}
          iconURL="/static/webapp/icons/alarm_bell.svg"
          iconID="nav_system_notifications"
          PopupComponent={MemoSystemNotifications}
        />

        <IconItem
          title="Help"
          iconURL="/static/webapp/icons/question_circle.svg"
          iconID="open_knowledge_base"
          PopupComponent={MemoHelpMenu}
        />

        {accountSetupVisibility && (
          <IconItem
            title="Account Setup"
            iconURL="/static/webapp/icons/task_list_settings_work_office_companies_18_1.svg"
            iconID="accountSetup-icon"
            badgeHasDone
            PopupComponent={MemoAccountSetup}
          />
        )}

        {groupSettingsVisibility ? (
          <IconItem
            title="Company & Group Settings"
            iconURL="/static/webapp/icons/cog_interface_essential.svg"
            iconID="settings_button"
            active={groupSettingsSelected || settingsSelected}
            PopupComponent={MemoGroupSettingsMenu}
          />
        ) : (
          <NavigationLink
            pathname={URL_CONST.settings.profile}
            id="settings_button"
            label="Settings"
          >
            <IconItem
              title="Settings"
              iconURL="/static/webapp/icons/cog_interface_essential.svg"
              iconID="settings_button"
              active={settingsSelected}
            />
          </NavigationLink>
        )}

        {partner && (
          <div className="navigation-item no-hover">
            <div className="navigation-item-content mw150 lt-tablet-bold" title={partner.name}>
              {partner.name}
            </div>
          </div>
        )}
        <DropdownItem id="username_dropdown" text={userEmail} items={userDropdownItems} />
      </div>
    </>
  )
}

function Navigation({
  theme,
  history,
  partner,
  companyName,
  userEmail,
  location,
  incidentsCount,
  userDropdownItems,
  hasLimitedPocView,

  incidentsVisibility,
  investigationVisibility,
  awarenessVisibility,
  mailboxesVisibility,
  groupSettingsVisibility,
  accountSetupVisibility,
  dmarcVisibility,
}) {
  const handleNavigationClick = (url, options) => {
    if (!options || !options.disableForMobile || window.innerWidth > parseInt(theme.rcPhonePoint)) {
      history.push(url)
    }
  }

  return (
    <div
      className={cls('navigation', {
        dn: location.pathname === URL_CONST.kpi.self,
      })}
    >
      <span
        onClick={() =>
          handleNavigationClick(partner ? URL_CONST.partners.self : URL_CONST.dashboard.home, {
            disableForMobile: true,
          })
        }
        className="navigation-logo cp"
      />
      <span className="icon navigation-toggle">
        <img src="/static/webapp/images/hamburger-icon.svg" />
      </span>
      <div id="main_navigation_toolbar" className="navigation-content">
        <RegularNav
          partner={partner}
          companyName={companyName}
          userEmail={userEmail}
          incidentsCount={incidentsCount}
          userDropdownItems={userDropdownItems}
          pathname={location.pathname}
          hasLimitedPocView={hasLimitedPocView}
          incidentsVisibility={incidentsVisibility}
          investigationVisibility={investigationVisibility}
          awarenessVisibility={awarenessVisibility}
          mailboxesVisibility={mailboxesVisibility}
          groupSettingsVisibility={groupSettingsVisibility}
          accountSetupVisibility={accountSetupVisibility}
          dmarcVisibility={dmarcVisibility}
        />
      </div>
    </div>
  )
}

export default withTheme(Navigation)
